import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Menu, X, Sun, Moon, User, LogOut, BookOpen,
  LayoutDashboard, Settings, Trophy, ChevronDown, Crown, Radio, Users, Phone
} from 'lucide-react';
import WhatsAppIcon from './WhatsAppIcon';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from '../context/TranslationContext';
import { getApiBaseUrl } from '@/lib/apiBase';

const API_URL = getApiBaseUrl();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const useStudentDirectoryEnabled = () => {
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/settings`);
        setEnabled(res?.data?.enable_student_directory !== false);
      } catch (err) {
        setEnabled(true);
      }
    };
    fetchSettings();
  }, []);

  return enabled;
};

const useLifetimePrice = () => {
  const [price, setPrice] = useState(4999);

  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const res = await axios.get(`${API_URL}/settings`);
        const lifetimePrice = res?.data?.pricing?.lifetime_price ?? res?.data?.lifetime_price;
        if (lifetimePrice) setPrice(lifetimePrice);
      } catch (err) {
        // keep default
      }
    };
    fetchPrice();
  }, []);

  return price;
};

export const Navbar = () => {
  const { user, logout, isAdmin, isPremium } = useAuth();
  const { t, tFormat } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const studentsEnabled = useStudentDirectoryEnabled();
  const lifetimePrice = useLifetimePrice();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 h-20 transition-all duration-300 ${
        scrolled ? 'bg-background/95 backdrop-blur-lg shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-full flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
            <span className="text-accent-foreground font-serif font-bold text-xl">T</span>
          </div>
          <span className="font-serif text-2xl font-medium tracking-tight group-hover:text-accent transition-colors">
            THACHARI
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          <Link 
            to="/courses" 
            data-testid="nav-courses"
            className={`text-sm font-medium transition-colors hover:text-accent ${
              isActive('/courses') ? 'text-accent' : 'text-muted-foreground'
            }`}
          >
            {t('nav_courses', 'Courses')}
          </Link>
          <Link 
            to="/projects" 
            data-testid="nav-projects"
            className={`text-sm font-medium transition-colors hover:text-accent ${
              isActive('/projects') ? 'text-accent' : 'text-muted-foreground'
            }`}
          >
            {t('nav_projects', 'Projects')}
          </Link>
          <Link
            to="/live-classes"
            data-testid="nav-live-classes"
            className={`text-sm font-medium transition-colors hover:text-accent flex items-center gap-1 ${
              isActive('/live-classes') ? 'text-accent' : 'text-muted-foreground'
            }`}
          >
            <Radio className="w-3 h-3" />
            {t('nav_live', 'Live')}
          </Link>
          {studentsEnabled && (
            <Link
              to="/students"
              data-testid="nav-students"
              className={`text-sm font-medium transition-colors hover:text-accent ${
                isActive('/students') ? 'text-accent' : 'text-muted-foreground'
              }`}
            >
              {t('nav_students', 'Our Students')}
            </Link>
          )}
          {user && (
            <>
              <Link 
                to="/dashboard" 
                data-testid="nav-dashboard"
                className={`text-sm font-medium transition-colors hover:text-accent ${
                  isActive('/dashboard') ? 'text-accent' : 'text-muted-foreground'
                }`}
              >
                {t('nav_dashboard', 'My Learning')}
              </Link>
              <Link 
                to="/leaderboard" 
                data-testid="nav-leaderboard"
                className={`text-sm font-medium transition-colors hover:text-accent ${
                  isActive('/leaderboard') ? 'text-accent' : 'text-muted-foreground'
                }`}
              >
                {t('nav_leaderboard', 'Leaderboard')}
              </Link>
            </>
          )}
        </div>

        {/* Right side */}
        <div className="hidden md:flex items-center gap-3">
          {/* Language Selector */}
          <LanguageSelector variant="ghost" />
          
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={toggleTheme}
            data-testid="theme-toggle"
            className="rounded-full"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-full gap-2 pl-2" data-testid="user-menu">
                  <Avatar className="w-6 h-6">
                    <AvatarImage src={user.photo_url ? `${BACKEND_URL}${user.photo_url}` : undefined} alt={user.name} />
                    <AvatarFallback className="text-xs">{user.name?.[0]?.toUpperCase() || <User className="w-3 h-3" />}</AvatarFallback>
                  </Avatar>
                  <span className="max-w-24 truncate">{user.name}</span>
                  {isPremium && <Crown className="w-4 h-4 text-amber-500" />}
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                  <p className="text-xs text-accent mt-1">
                    {user.points} {t('nav_points', 'points')}
                  </p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  {t('menu_dashboard', 'Dashboard')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/bookmarks')} data-testid="menu-bookmarks">
                  <BookOpen className="w-4 h-4 mr-2" />
                  {t('menu_bookmarks', 'Bookmarks')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                  <User className="w-4 h-4 mr-2" />
                  {t('menu_profile', 'Profile')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/affiliate')} data-testid="menu-affiliate">
                  <Trophy className="w-4 h-4 mr-2" />
                  {t('menu_refer_earn', 'Refer & Earn')}
                </DropdownMenuItem>
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                      <Settings className="w-4 h-4 mr-2" />
                      {t('nav_admin', 'Admin Panel')}
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('nav_logout', 'Logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/login')} data-testid="login-btn">
                {t('nav_sign_in', 'Sign In')}
              </Button>
              <Button
                onClick={() => { window.location.href = 'https://thachari.com/signup' }}
                onTouchStart={() => { window.location.href = 'https://thachari.com/signup' }}
                data-testid="signup-btn"
                className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
              >
                {tFormat(
                  'nav_get_access',
                  { price: `₹${Number(lifetimePrice).toLocaleString('en-IN')}` },
                  'Get Access — {price}'
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Mobile menu button */}
        <Button 
          variant="ghost" 
          size="icon" 
          className="md:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-20 left-0 right-0 bg-background border-b shadow-lg">
          <div className="px-6 py-4 space-y-4">
            <Link to="/courses" className="block py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
              {t('nav_courses', 'Courses')}
            </Link>
            <Link to="/projects" className="block py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
              {t('nav_projects', 'Projects')}
            </Link>
            <Link to="/live-classes" className="block py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
              {t('nav_live_classes', 'Live Classes')}
            </Link>
            {studentsEnabled && (
              <Link to="/students" className="block py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                {t('nav_students', 'Our Students')}
              </Link>
            )}
            {user && (
              <>
                <Link to="/dashboard" className="block py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                  {t('nav_dashboard', 'My Learning')}
                </Link>
                <Link to="/leaderboard" className="block py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                  {t('nav_leaderboard', 'Leaderboard')}
                </Link>
                <Link to="/profile" className="block py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                  {t('menu_profile', 'Profile')}
                </Link>
                {isAdmin && (
                  <Link to="/admin" className="block py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                    {t('nav_admin', 'Admin Panel')}
                  </Link>
                )}
              </>
            )}
            <div className="flex items-center gap-3 pt-4 border-t">
              <LanguageSelector variant="outline" />
              <Button variant="ghost" size="sm" onClick={toggleTheme}>
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>
              {user ? (
                <Button variant="outline" size="sm" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('nav_logout', 'Logout')}
                </Button>
              ) : (
                <Button size="sm" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>
                  {t('nav_sign_in', 'Sign In')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export const Footer = () => {
  const { t } = useTranslation();
  const studentsEnabled = useStudentDirectoryEnabled();

  return (
    <footer className="border-t bg-card" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
                <span className="text-accent-foreground font-serif font-bold text-xl">T</span>
              </div>
              <span className="font-serif text-2xl font-medium">THACHARI</span>
            </div>
            <p className="text-muted-foreground text-sm max-w-md">
              {t(
                'footer_tagline',
                'Master the art of interior design and carpentry with expert-led courses. From materials to finishing techniques, learn everything you need to transform spaces.'
              )}
            </p>
            <div className="flex flex-col gap-1.5 mt-4">
              <a href="tel:+919626722220" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors">
                <Phone className="w-3.5 h-3.5" /> +91 96267 22220
              </a>
              <a href="tel:+919626822220" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors">
                <Phone className="w-3.5 h-3.5" /> +91 96268 22220
              </a>
              <div className="flex items-center gap-2 mt-1">
                <a href="https://wa.me/919626722220" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-[#25D366] text-white rounded-full px-3 py-1 text-xs font-semibold hover:opacity-90 transition-opacity">
                  <WhatsAppIcon className="w-3.5 h-3.5" /> 96267 22220
                </a>
                <a href="https://wa.me/919626822220" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-[#25D366] text-white rounded-full px-3 py-1 text-xs font-semibold hover:opacity-90 transition-opacity">
                  <WhatsAppIcon className="w-3.5 h-3.5" /> 96268 22220
                </a>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">{t('footer_quick_links', 'Quick Links')}</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/courses" className="hover:text-accent transition-colors">{t('footer_all_courses', 'All Courses')}</Link></li>
              <li><Link to="/leaderboard" className="hover:text-accent transition-colors">{t('footer_leaderboard', 'Leaderboard')}</Link></li>
              <li><Link to="/pricing" className="hover:text-accent transition-colors">{t('footer_pricing', 'Pricing')}</Link></li>
              {studentsEnabled && (
                <li><Link to="/students" className="hover:text-accent transition-colors">{t('footer_our_students', 'Our Students')}</Link></li>
              )}
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">{t('footer_categories', 'Categories')}</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/courses?category=materials" className="hover:text-accent transition-colors">{t('footer_materials', 'Materials')}</Link></li>
              <li><Link to="/courses?category=furniture" className="hover:text-accent transition-colors">{t('footer_furniture', 'Furniture Design')}</Link></li>
              <li><Link to="/courses?category=interior" className="hover:text-accent transition-colors">{t('footer_interior_planning', 'Interior Planning')}</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            {t('footer_rights', '© 2025 THACHARI. Master Interior Design & Carpentry.')}
          </p>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <Link to="/privacy" className="hover:text-accent transition-colors">{t('footer_privacy', 'Privacy')}</Link>
            <Link to="/terms" className="hover:text-accent transition-colors">{t('footer_terms', 'Terms')}</Link>
            <Link to="/refund-policy" className="hover:text-accent transition-colors">{t('footer_refund_policy', 'Refund Policy')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
